import React, { useState, useEffect } from 'react';
import { 
  Card, Button, Row, Col, Table, 
  Badge, Alert, Spinner, Accordion,
  Pagination, Tabs, Tab
} from 'react-bootstrap';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  getCampaign, getCampaignDynamic, getFilteredLeads,
  generateJourneyUrls, downloadLeads, getAvailableFilters, getFilteredLeadsDynamic  
} from '../../services/api';

const CampaignDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [campaign, setCampaign] = useState(null);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [loading, setLoading] = useState(true);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [error, setError] = useState('');
  const [urlGenerating, setUrlGenerating] = useState(false);
  const [urlGenerateStatus, setUrlGenerateStatus] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [availableFilters, setAvailableFilters] = useState(null);
  
  const PAGE_SIZE = 10;

  useEffect(() => {
    const fetchFilterMapping = async () => {
      try {
        const filters = await getAvailableFilters();
        setAvailableFilters(filters);
      } catch (err) {
        console.error("Error fetching filter mapping:", err);
      }
    };

    fetchFilterMapping();
  }, []);
  
  useEffect(() => {
    const fetchCampaign = async () => {
      setLoading(true);
      try {
        let data;
        try {
          data = await getCampaignDynamic(id);
        } catch (dynamicError) {
          // If dynamic fails, fall back to basic campaign
          data = await getCampaign(id);
        }

        setCampaign(data);
      } catch (err) {
        console.error("Error fetching campaign:", err);
        setError("Failed to load campaign details.");
      } finally {
        setLoading(false);
      }
    };

    fetchCampaign();
  }, [id]);

  useEffect(() => {
    if (!campaign) return;

    const fetchLeads = async () => {
      setLeadsLoading(true);
      try {
        let data;

        // Check if this is a dynamic campaign (same logic as filter display)
        const filterCriteria = campaign.filter_criteria;
        const isDynamicCampaign =
          typeof filterCriteria === "object" &&
          filterCriteria !== null &&
          !filterCriteria.hasOwnProperty("min_bureau_score") &&
          !filterCriteria.hasOwnProperty("max_bureau_score");

        console.log("=== LEADS FETCH DEBUG ===");
        console.log("Is dynamic campaign for leads:", isDynamicCampaign);
        console.log("Filter criteria:", filterCriteria);

        if (isDynamicCampaign) {
          // Use dynamic API for advanced campaigns
          console.log("Using dynamic leads API");
          data = await getFilteredLeadsDynamic(id, {
            skip: (page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
          });
        } else {
          // Use old API for basic campaigns
          console.log("Using basic leads API");
          data = await getFilteredLeads(id, {
            skip: (page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
          });
        }

        console.log("Leads data received:", data);
        console.log("Total leads:", data.total);

        setFilteredLeads(data.leads);
        setTotalLeads(data.total);
        setTotalPages(Math.ceil(data.total / PAGE_SIZE));
      } catch (err) {
        console.error("Error fetching leads:", err);
      } finally {
        setLeadsLoading(false);
      }
    };

    fetchLeads();
  }, [id, page, campaign]);

  const getFilterDisplayName = (filterId) => {
    if (!availableFilters || !availableFilters.categories) return filterId;

    // Search through all categories to find the filter
    for (const category of Object.values(availableFilters.categories)) {
      const filter = category.find((f) => f.id === filterId);
      if (filter) {
        return filter.name; // This will return "Age" instead of "dm001s"
      }
    }

    return filterId; // Fallback to original ID if not found
  };
  
  const handleGenerateUrls = async () => {
    setUrlGenerating(true);
    setUrlGenerateStatus(null);
    
    try {
      const result = await generateJourneyUrls(id);
      setUrlGenerateStatus({
        success: true,
        message: `Generated ${result.generated_count} URLs out of ${result.total_leads} leads.`
      });
      
      // Refresh leads data to show new URLs
      const data = await getFilteredLeads(id, {
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE
      });
      setFilteredLeads(data.leads);
    } catch (err) {
      console.error('Error generating URLs:', err);
      setUrlGenerateStatus({
        success: false,
        message: 'Failed to generate URLs. Please try again.'
      });
    } finally {
      setUrlGenerating(false);
    }
  };
  
  const handleDownload = async (includeUrls = true) => {
    try {
      const data = await downloadLeads(id, includeUrls);
      
      // Create and trigger download
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `campaign_${id}_leads.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading leads:', err);
      alert('Failed to download leads. Please try again.');
    }
  };
  
  const renderStatus = (status) => {
    if (status === 'active') {
      return <Badge bg="success">Active</Badge>;
    } else if (status === 'inactive') {
      return <Badge bg="secondary">Inactive</Badge>;
    } else if (status === 'completed') {
      return <Badge bg="primary">Completed</Badge>;
    }
    return <Badge bg="secondary">{status}</Badge>;
  };
  
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };
  
  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }
  
  if (error) {
    return (
      <Alert variant="danger">
        {error}
        <div className="mt-3">
          <Link to="/campaigns">
            <Button variant="primary">Back to Campaigns</Button>
          </Link>
        </div>
      </Alert>
    );
  }
  
  if (!campaign) {
    return (
      <Alert variant="warning">
        Campaign not found.
        <div className="mt-3">
          <Link to="/campaigns">
            <Button variant="primary">Back to Campaigns</Button>
          </Link>
        </div>
      </Alert>
    );
  }
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{campaign.name}</h2>
        <div>
          <Button
            variant="outline-secondary"
            className="me-2"
            onClick={() => navigate("/campaigns")}
          >
            Back to Campaigns
          </Button>
        </div>
      </div>

      <Tabs defaultActiveKey="details" className="mb-3">
        <Tab eventKey="details" title="Campaign Details">
          <Card className="mb-4">
            <Card.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <h6>Bank</h6>
                  <p className="text-primary fw-bold">{campaign.bank}</p>
                </Col>
                <Col md={6}>
                  <h6>Process</h6>
                  <p className="text-primary fw-bold">{campaign.process}</p>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <h6>Created</h6>
                  <p>{formatDate(campaign.created_at)}</p>
                </Col>
                <Col md={6}>
                  <h6>Status</h6>
                  <p>{renderStatus(campaign.status)}</p>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <h6>Total Leads</h6>
                  <p className="fw-bold">{campaign.lead_count}</p>
                </Col>
              </Row>

              <hr className="my-4" />

              <h5>Filter Criteria</h5>
              {(() => {

                const filterCriteria = campaign.filter_criteria;

                // Check if this is a dynamic campaign
                const isDynamicCampaign =
                  typeof filterCriteria === "object" &&
                  filterCriteria !== null &&
                  !filterCriteria.hasOwnProperty("min_bureau_score") &&
                  !filterCriteria.hasOwnProperty("max_bureau_score");


                if (isDynamicCampaign) {
                  // Display dynamic filters with proper names
                  const activeFilters = Object.entries(filterCriteria);

                  if (activeFilters.length === 0) {
                    return <p className="text-muted">No filters applied</p>;
                  }

                  return (
                    <Row className="mb-3">
                      {activeFilters.map(([filterId, value]) => {
                        const displayName = getFilterDisplayName(filterId);

                        return (
                          <Col md={4} key={filterId} className="mb-3">
                            <h6>{displayName}</h6>
                            <small className="text-muted d-block">
                              {filterId}
                            </small>
                            <p className="mb-0">
                              {typeof value === "object" &&
                              value !== null &&
                              value.min !== undefined &&
                              value.max !== undefined
                                ? `${value.min} - ${value.max}`
                                : String(value)}
                            </p>
                          </Col>
                        );
                      })}
                    </Row>
                  );
                } else {
                  // Display basic campaign filters (existing code)
                  return (
                    <Row className="mb-3">
                      {filterCriteria?.min_bureau_score !== null &&
                        filterCriteria?.min_bureau_score !== undefined && (
                          <Col md={3}>
                            <h6>Min Bureau Score</h6>
                            <p>{filterCriteria.min_bureau_score}</p>
                          </Col>
                        )}
                      {/* ... rest of basic filter displays ... */}
                    </Row>
                  );
                }
              })()}
              <Row className="mb-3 d-none">
                {campaign.filter_criteria.min_bureau_score !== null && (
                  <Col md={3}>
                    <h6>Min Bureau Score</h6>
                    <p>{campaign.filter_criteria.min_bureau_score}</p>
                  </Col>
                )}

                {campaign.filter_criteria.max_dpd !== null && (
                  <Col md={3}>
                    <h6>Max DPD</h6>
                    <p>{campaign.filter_criteria.max_dpd}</p>
                  </Col>
                )}

                {campaign.filter_criteria.max_foir !== null && (
                  <Col md={3}>
                    <h6>Max FOIR</h6>
                    <p>{campaign.filter_criteria.max_foir}%</p>
                  </Col>
                )}

                {campaign.filter_criteria.is_salaried !== null && (
                  <Col md={3}>
                    <h6>Employment Type</h6>
                    <p>
                      {campaign.filter_criteria.is_salaried
                        ? "Salaried"
                        : "Self-employed"}
                    </p>
                  </Col>
                )}
              </Row>

              {campaign.filter_criteria.loan_types &&
                campaign.filter_criteria.loan_types.length > 0 && (
                  <Row>
                    <Col>
                      <h6>Loan Types</h6>
                      <p>
                        {campaign.filter_criteria.loan_types.map((type) => (
                          <Badge key={type} bg="info" className="me-2">
                            {type}
                          </Badge>
                        ))}
                      </p>
                    </Col>
                  </Row>
                )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="leads" title="Filtered Leads">
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0">Filtered Leads ({totalLeads})</h5>
                <div>
                  <Button
                    variant="success"
                    className="me-2"
                    onClick={handleGenerateUrls}
                    disabled={urlGenerating}
                  >
                    {urlGenerating ? "Generating..." : "Generate Journey URLs"}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => handleDownload(true)}
                  >
                    Download with URLs
                  </Button>
                </div>
              </div>

              {urlGenerateStatus && (
                <Alert
                  variant={urlGenerateStatus.success ? "success" : "danger"}
                  className="mb-4"
                >
                  {urlGenerateStatus.message}
                </Alert>
              )}

              {leadsLoading ? (
                <div className="text-center p-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : filteredLeads.length === 0 ? (
                <div className="text-center p-5">
                  <p>No leads match the filter criteria.</p>
                </div>
              ) : (
                <>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Mobile</th>
                        <th>PAN</th>
                        <th>Score</th>
                        <th>Age</th>
                        <th>Gender</th>
                        <th>Occupation</th>
                        <th>Income</th>
                        <th>Journey URL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLeads.map((lead) => (
                        <tr key={lead.lead_id}>
                          <td>{lead.name}</td>
                          <td>{lead.mobile}</td>
                          <td>{lead.pan || "-"}</td>
                          <td>{lead.bureau_score || "-"}</td>
                          <td>{lead.age || "-"}</td>
                          <td>{lead.gender || "-"}</td>
                          <td>{lead.occupation || "-"}</td>
                          <td>{lead.income ? `₹${lead.income}` : "-"}</td>
                          <td>
                            {lead.short_url ? (
                              <a
                                href={lead.short_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {lead.short_url}
                              </a>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                      <Pagination>
                        <Pagination.First
                          onClick={() => setPage(1)}
                          disabled={page === 1}
                        />
                        <Pagination.Prev
                          onClick={() =>
                            setPage((prev) => Math.max(prev - 1, 1))
                          }
                          disabled={page === 1}
                        />

                        {[...Array(totalPages)].map((_, i) => (
                          <Pagination.Item
                            key={i + 1}
                            active={i + 1 === page}
                            onClick={() => setPage(i + 1)}
                          >
                            {i + 1}
                          </Pagination.Item>
                        ))}

                        <Pagination.Next
                          onClick={() =>
                            setPage((prev) => Math.min(prev + 1, totalPages))
                          }
                          disabled={page === totalPages}
                        />
                        <Pagination.Last
                          onClick={() => setPage(totalPages)}
                          disabled={page === totalPages}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </div>
  );
};

export default CampaignDetailPage;